import { Fragment, type ReactNode } from 'react'
import { CTASection } from '@/components/landing/CTASection'
import { StoryCard } from '@/components/landing/service'
import { Highlight, type HighlightColor } from '@/components/landing/about/Highlight'
import type { SolutionData } from '@/data/solutions'
import type { CustomerStory } from '@/data/customers'

/** Props every solution layout receives. */
export interface SolutionLayoutProps {
  solution: SolutionData
  /** The featured customer story matched from data/customers. */
  story: CustomerStory
}

/** Split a heading so its last word can sit in a highlight box. */
export function splitLastWord(text: string): [string, string] {
  const i = text.lastIndexOf(' ')
  return i === -1 ? ['', text] : [text.slice(0, i), text.slice(i + 1)]
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Render `text` with each phrase in `phrases` set in an inline highlight box,
 * cycling through `colors`. The text itself is unchanged, so the element's
 * accessible name stays the plain sentence.
 */
export function withHighlights(
  text: string,
  phrases: string[] = [],
  colors: HighlightColor[] = ['pink', 'blue']
): ReactNode {
  if (phrases.length === 0) return text
  const pattern = new RegExp(`(${phrases.map(escape).join('|')})`, 'g')
  let n = 0
  return text.split(pattern).map((part, i) => {
    if (!phrases.includes(part)) return <Fragment key={i}>{part}</Fragment>
    const color = colors[n++ % colors.length]!
    return (
      <Highlight key={i} color={color}>
        {part}
      </Highlight>
    )
  })
}

/** The page's featured customer story as the FAQ aside card. */
export function SolutionStoryCard({ solution, story }: SolutionLayoutProps) {
  return (
    <StoryCard
      company={solution.customerStory.company}
      industry={story.industry}
      headline={story.headline}
      quote={solution.customerStory.quote}
      author={solution.customerStory.author}
      authorTitle={solution.customerStory.title}
      metric={{
        label: solution.customerStory.improvement,
        before: solution.customerStory.beforeMetric,
        after: solution.customerStory.afterMetric,
      }}
      href="/customers"
    />
  )
}

const CTA_TONES = {
  blue: { variant: 'accent', className: undefined },
  purple: { variant: 'accent', className: 'bg-[var(--color-landing-secondary)] dark:bg-[var(--color-landing-secondary)]' },
  pink: { variant: 'accent', className: 'bg-[#ff00f2] dark:bg-[#ff00f2]' },
  dark: { variant: 'dark', className: undefined },
} as const

/** The closing CTA band shared by every solution page, in the layout's colour. */
export function SolutionCta({ solution, tone = 'blue' }: { solution: SolutionData; tone?: keyof typeof CTA_TONES }) {
  const { variant, className } = CTA_TONES[tone]
  return (
    <CTASection
      variant={variant}
      align="left"
      className={className}
      title={solution.ctaHeadline}
      description={solution.ctaDescription}
      buttons={[
        { label: 'Book a demo', href: '/contact', variant: 'primary' },
        { label: 'Start free trial', href: '/register', variant: 'outline' },
      ]}
    />
  )
}

/** Accessible label for the how-it-works region (tests and screen readers key on it). */
export const stepsLabel = (solution: SolutionData) =>
  `How Templatean works for ${solution.teamName.toLowerCase()} teams`

/** Faded body colour from the reference (66% of the ink). */
export const FADED =
  'text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]'

/** Quote-style intro paragraph: thin left rule, faded ink. */
export const QUOTE = `border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg dark:border-[var(--color-landing-gray-700)] ${FADED}`
